import { Bar, BarChart as RBarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ChartAccessibleTable, ChartFrame } from './ChartFrame'

export type WaterfallStepKind = 'base' | 'increase' | 'decrease' | 'subtotal' | 'total'

export interface WaterfallStep {
  key: string
  label: string
  /** Always the step's own magnitude (never signed) — `kind` decides direction/placement. */
  value: number
  kind: WaterfallStepKind
}

export interface WaterfallChartProps {
  title: string
  steps: WaterfallStep[]
  valueFormatter?: (value: number) => string
  onStepClick?: (step: WaterfallStep) => void
  height?: number
}

/**
 * New chart primitive for IB-282 View 2 (docs/design/IB-282-view2-per-case-design.md §5) — the
 * credit-computation "base → uplifts → cap → net" breakdown (DoR AC2.2). No waterfall existed in
 * `charts/` before this; built to the exact same `ChartFrame`/`ChartAccessibleTable` contract as
 * `BarChart`/`DonutChart` so it drops into the existing dashboard system, not a one-off.
 *
 * Direction is conveyed by BOTH a token color AND a `+`/`-`/`=` label prefix (never color alone,
 * design-system.md §7) — `base`/`subtotal`/`total` (checkpoint bars, anchored at 0) use
 * `color.data.1`, `increase` steps use `color.data.2`, `decrease` steps use `color.data.3`. This
 * deliberately does NOT reach for a red/green diverging pair: design-system.md §5.2 states a
 * diverging (delta) scale is "not yet specified — treat as a follow-on design task," and
 * `color.status.*` is reserved for status, never a chart series (CLAUDE.md §4). Ships today on the
 * 4 ratified categorical tokens instead of waiting on that follow-on ramp.
 *
 * Each visible bar is a `[base, base+value]` (or `[base-value, base]` for `decrease`) stacked
 * segment on top of an invisible offset bar — the standard "floating bar" waterfall technique.
 * Purely presentational: the caller (a future data-wiring pass) computes `steps` from the real,
 * pinned `ComputationRunView`/node provenance (never a re-execution) and passes them in.
 */
export function WaterfallChart({ title, steps, valueFormatter = (v) => String(v), onStepClick, height = 280 }: WaterfallChartProps) {
  let running = 0
  const rows = steps.map((step) => {
    let base: number
    let barValue: number
    let endingRunning: number
    if (step.kind === 'increase') {
      base = running
      barValue = step.value
      endingRunning = running + step.value
    } else if (step.kind === 'decrease') {
      base = running - step.value
      barValue = step.value
      endingRunning = running - step.value
    } else {
      // base | subtotal | total — an absolute checkpoint, anchored at 0.
      base = 0
      barValue = step.value
      endingRunning = step.value
    }
    running = endingRunning
    const signPrefix = step.kind === 'increase' ? '+' : step.kind === 'decrease' ? '−' : '='
    return { ...step, base, barValue, runningTotal: endingRunning, signPrefix }
  })

  const table = (
    <ChartAccessibleTable
      caption={`${title} — data table`}
      columns={[
        { key: 'label', label: 'Step' },
        { key: 'kind', label: 'Type' },
        { key: 'amount', label: 'Amount', align: 'end' },
        { key: 'runningTotal', label: 'Running total', align: 'end' },
      ]}
      rows={rows.map((r) => ({
        label: r.label,
        kind: r.kind,
        amount: `${r.signPrefix} ${valueFormatter(r.barValue)}`,
        runningTotal: valueFormatter(r.runningTotal),
      }))}
    />
  )

  const fillFor = (kind: WaterfallStepKind): string =>
    kind === 'increase' ? 'var(--iw-chart-2)' : kind === 'decrease' ? 'var(--iw-chart-3)' : 'var(--iw-chart-1)'

  return (
    <ChartFrame
      title={title}
      height={height}
      table={table}
      chart={
        <ResponsiveContainer width="100%" height="100%">
          <RBarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--iw-border)" />
            <XAxis dataKey="label" tick={{ fill: 'var(--iw-text-muted)', fontSize: 12 }} interval={0} />
            <YAxis tick={{ fill: 'var(--iw-text-muted)', fontSize: 12 }} tickFormatter={valueFormatter} />
            <Tooltip formatter={(value) => valueFormatter(Number(value))} />
            <Bar dataKey="base" stackId="waterfall" fill="transparent" isAnimationActive={false} />
            <Bar
              dataKey="barValue"
              stackId="waterfall"
              isAnimationActive={false}
              cursor={onStepClick ? 'pointer' : undefined}
              onClick={(entry) => onStepClick?.((entry as { payload: WaterfallStep }).payload)}
            >
              {rows.map((r) => (
                <Cell key={r.key} fill={fillFor(r.kind)} />
              ))}
              <LabelList
                dataKey="barValue"
                position="top"
                formatter={(value) => valueFormatter(Number(value))}
                style={{ fill: 'var(--iw-text-muted)', fontSize: 11 }}
              />
            </Bar>
          </RBarChart>
        </ResponsiveContainer>
      }
    />
  )
}
